import type { MapboxOverlay } from '@deck.gl/mapbox'
import type { PointLike } from 'maplibre-gl'
import type { MapRef } from 'react-map-gl/maplibre'
import {
  FLOOD_DEPTH_BREAKS,
  isAdaptationLayerId,
  isAdaptationPmTileSource,
  isBuildingLayerId,
  isBuildingPmTileSource,
  isFloodContourSource,
  PMTileSources,
} from '~/constants'
import { buildingDamagesForScenario } from '~/util/buildingDamages'
import { floodDepthLabelForContour } from '~/util/floodContours'

export type MapTooltipQueryInput = {
  mapRef: MapRef | null
  deckRef: MapboxOverlay | null
  point: PointLike
  DEM: string | undefined
  returnPeriod: string | undefined
  adaptation: string | undefined
  /** Contour depth tooltips only when Depth mode is on (opacity-0 fills are still queryable). */
  contourFloodEnabled: boolean
}

export type MapTooltipQueryResult = {
  wavePointName: string | null
  adaptationType: string | null
  buildingOcctype: string | null
  buildingCategories: string[] | null
  buildingProperties: GeoJSON.GeoJsonProperties | null
  buildingSource: string | null
  totalDamages: number | null
  population: number | null
  floodDepthContour: number | null
  floodDepthLabel: string | null
}

function parseBuildingCategories(raw: unknown): string[] | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  }
  if (Array.isArray(raw)) return raw as string[]
  return null
}

/** Shared hit-test for hover/click map tooltips. */
export function queryMapTooltipAtPoint(input: MapTooltipQueryInput): MapTooltipQueryResult {
  const { mapRef, deckRef, point, DEM, returnPeriod, adaptation, contourFloodEnabled } = input
  const features = mapRef?.queryRenderedFeatures(point) ?? []

  let waveFeatures: Array<{ properties: { [name: string]: string | number } }> = features.filter(
    (f) => f.source === PMTileSources.WAVES || f.layer?.id === PMTileSources.WAVES
  )

  if (!waveFeatures.length && deckRef) {
    const p = point as { x: number; y: number }
    waveFeatures = deckRef.pickMultipleObjects({ x: p.x, y: p.y }).map(({ object }) => object)
  }

  const buildingFeature = features.find(
    (f) => isBuildingPmTileSource(String(f.source)) || isBuildingLayerId(f.layer?.id)
  )
  const adaptationFeature = features.find(
    (f) => isAdaptationPmTileSource(String(f.source)) || isAdaptationLayerId(f.layer?.id)
  )
  const rawAdaptationType = adaptationFeature?.properties?.adaptation
  const adaptationType: string | null =
    rawAdaptationType != null && rawAdaptationType !== 'none' ? String(rawAdaptationType) : null
  const rawOcctype = buildingFeature?.properties?.occtype
  const buildingOcctype: string | null = rawOcctype != null ? String(rawOcctype) : null
  const buildingCategories = parseBuildingCategories(
    buildingFeature?.properties?.building_categories
  )
  const buildingProperties = buildingFeature?.properties ?? null
  const buildingSource = buildingFeature != null ? String(buildingFeature.source) : null
  const scenarioSuffix = `dem${DEM ?? ''}_rp${returnPeriod ?? ''}_adapt${adaptation ?? ''}`
  const totalDamages = buildingDamagesForScenario(buildingProperties, scenarioSuffix)
  const rawPopulation = buildingProperties?.population
  const population: number | null =
    rawPopulation != null && !Number.isNaN(Number(rawPopulation)) ? Number(rawPopulation) : null

  let floodDepthContour: number | null = null
  if (contourFloodEnabled) {
    const contours = features
      .filter((f) => isFloodContourSource(String(f.source)))
      .map((f) => Number(f.properties?.contour))
      .filter((n) => Number.isFinite(n))
    if (contours.length) floodDepthContour = Math.max(...contours)
  }
  const floodDepthLabel =
    floodDepthContour != null
      ? floodDepthLabelForContour(floodDepthContour, FLOOD_DEPTH_BREAKS)
      : null

  return {
    wavePointName:
      waveFeatures[0]?.properties.point_name != null
        ? String(waveFeatures[0].properties.point_name)
        : null,
    adaptationType,
    buildingOcctype,
    buildingCategories,
    buildingProperties,
    buildingSource,
    totalDamages,
    population,
    floodDepthContour: floodDepthLabel != null ? floodDepthContour : null,
    floodDepthLabel,
  }
}

/** Fields that can still open a map tooltip. Buildings are color-coded only. */
export type MapTooltipTargetFields = {
  wavePointName?: string | null
  adaptationType?: string | null
  floodDepthLabel?: string | null
}

export function hasMapTooltipTarget(r: MapTooltipTargetFields): boolean {
  return !!r.wavePointName || !!r.adaptationType || r.floodDepthLabel != null
}
